import { createAsyncThunk } from '@reduxjs/toolkit'
import { handleError, hasError } from '@repo/shared/api/helpers'

import { AsyncThunkConfig, RejectValue } from '@/store/actions'
import { Resource } from '@/store/admin'
import { ResourceFields } from '@/store/spi/api'

export const resource = {
  admin: {
    create: createAsyncThunk<
      { resource: Resource },
      Pick<Resource, 'content' | 'title'>,
      AsyncThunkConfig
    >(
      'ADMIN_CREATE_RESOURCE',
      async (fields, { extra, fulfillWithValue, rejectWithValue, getState }) => {
        const { altApi } = extra
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const resource = await altApi.resources.createOne({ organizationId, fields })
        if (hasError(resource)) return rejectWithValue(handleError(resource))
        return fulfillWithValue({ resource })
      },
    ),
    delete: createAsyncThunk<
      { isDeleted: boolean; notify: boolean },
      { resourceToDelete: Resource; notify?: boolean },
      AsyncThunkConfig
    >(
      'ADMIN_DELETE_RESOURCE',
      async (
        { resourceToDelete, notify = true },
        { extra, fulfillWithValue, rejectWithValue, getState },
      ) => {
        const { altApi } = extra
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const res = await altApi.resources.deleteOne({
          organizationId,
          resourceId: resourceToDelete.id,
        })
        if (hasError(res)) return rejectWithValue(handleError(res))
        return fulfillWithValue({ isDeleted: true, notify })
      },
    ),
    restore: createAsyncThunk<
      { resource: Resource; notify?: boolean },
      { resourceToRestore: Resource; notify?: boolean },
      AsyncThunkConfig
    >(
      'ADMIN_RESTORE_RESOURCE',
      async (
        { resourceToRestore, notify = true },
        { getState, extra, fulfillWithValue, rejectWithValue },
      ) => {
        const { altApi } = extra
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const resource = await altApi.resources.restoreOne({
          organizationId,
          resourceId: resourceToRestore.id,
        })
        if (hasError(resource)) return rejectWithValue(handleError(resource))
        return fulfillWithValue({ resource, notify })
      },
    ),
    update: createAsyncThunk<
      { resource: Resource },
      { resourceId: string; fields: ResourceFields },
      AsyncThunkConfig
    >(
      'ADMIN_UPDATE_RESOURCE',
      async ({ resourceId, fields }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
        const { altApi } = extra
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const resource = await altApi.resources.updateOne({
          organizationId,
          resourceId,
          fields,
        })
        if (hasError(resource)) return rejectWithValue(handleError(resource))
        return fulfillWithValue({ resource })
      },
    ),
  },
}
